import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import DataArrayRoundedIcon from '@mui/icons-material/DataArrayRounded';
import MenuList from '@components/MenuList';
import Tooltip from '@components/Tooltip';
import { useInsertPlaceholder, usePlaceholderContext } from '@components/TextEditor';
import { useTranslation } from '@hooks';
import { getPlaceholdersForContext } from '@constants/placeholders';

const PostDynamicVariables = ({ btnStyles = {}, disabled, hasLocation = true }) => {
    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const [anchorEl, setAnchorEl] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const { t } = useTranslation();
    const insertToken = useInsertPlaceholder();
    const context = usePlaceholderContext();
    const surveysEnabled = useSelector(state => !!state.account?.account?.surveysEnabled);

    const handleOpen = event => {
        if (isMobile) {
            setOpenModal(true);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => setAnchorEl(null);
    const handleCloseModal = () => setOpenModal(false);

    const placeholders = useMemo(
        () =>
            getPlaceholdersForContext(context).filter(
                p => !p.hasRefId && (hasLocation || !p.requiresLocation) && (surveysEnabled || !p.requiresSurveys),
            ),
        [context, hasLocation, surveysEnabled],
    );

    const options = useMemo(
        () =>
            placeholders.map(({ labelKey, token, requiresLocation }, index) => ({
                icon: null,
                text: t(labelKey),
                divider: index > 0 && !!requiresLocation && !placeholders[index - 1].requiresLocation,
                sub: {},
                onClick: () => {
                    insertToken(token);
                    handleClose();
                    handleCloseModal();
                },
            })),
        [t, placeholders, insertToken],
    );

    if (options.length === 0) return null;

    return (
        <MenuList
            options={options}
            anchorEl={anchorEl}
            onCloseMenu={handleClose}
            openModal={openModal}
            handleCloseModal={handleCloseModal}
            mainTitle={t('PostDynamicVariables.title')}
            menuButton={
                <Tooltip title={t('PostDynamicVariables.title')} placement="top">
                    <IconButton
                        className="pl-post-variables-btn"
                        size="small"
                        color="inherit"
                        onClick={handleOpen}
                        disabled={disabled}
                        aria-label={t('PostDynamicVariables.title')}
                        sx={{
                            height: 18,
                            width: 18,
                            padding: '2px !important',
                            '& svg': {
                                color: 'var(--text-secondary)',
                                fontSize: 16,
                            },
                            ...btnStyles,
                        }}
                    >
                        <DataArrayRoundedIcon />
                    </IconButton>
                </Tooltip>
            }
        />
    );
};

export default PostDynamicVariables;
